"use client";
import { useCallback, useEffect, useState } from "react";
import {
  FieldValues,
  Path,
  SetValueConfig,
  UseFormReturn,
} from "react-hook-form-mui";

export const SESSION_PREFIX = "kei-form-";

export interface UseFormPersistOptions {
  setValueOptions?: SetValueConfig;
  deleteOnLeave?: boolean;
}

export const useFormPersist = <TFieldValues extends FieldValues = FieldValues>(
  name: string,
  form: UseFormReturn<TFieldValues>,
  { setValueOptions, deleteOnLeave }: UseFormPersistOptions = {},
) => {
  const [isLoading, setIsLoading] = useState(true);
  const key = SESSION_PREFIX + name;

  const reload = useCallback(():
    | Record<Path<TFieldValues>, unknown>
    | undefined => {
    let data: Record<string, unknown> | undefined;
    try {
      data = JSON.parse(sessionStorage.getItem(key) || "");
    } catch (e) {
      // missing store or not readable
    }

    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        form.setValue(key as any, value as any, setValueOptions);
      });
    }

    return data;
  }, [form.setValue, key]);

  const clear = useCallback(() => {
    sessionStorage.removeItem(key);
  }, [key]);

  const data = form.watch();

  useEffect(() => {
    if (isLoading) {
      reload();
      setIsLoading(false);
    } else {
      const dirty = Object.keys(form.formState.dirtyFields);
      if (dirty.length > 0) {
        sessionStorage.setItem(key, JSON.stringify(data));
      } else {
        sessionStorage.removeItem(key);
      }
    }
  }, [reload, key, data, isLoading, form.formState.dirtyFields]);

  // delete the session once done
  useEffect(() => {
    return () => {
      if (deleteOnLeave) {
        clear();
      }
    };
  }, []);

  return {
    isLoading,
    reload,
    clear,
  };
};
